import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FieldValues, useForm } from 'react-hook-form'
import { ColumnDef } from '@tanstack/react-table'
import { EyeIcon } from '@heroicons/react/24/outline'
import { CustomTable } from 'components/customTable'
import { DialogWrapper } from 'components/dialogWrapper'
import { SmoothTransition } from 'components/pageTransition'
import { PagePreloader } from 'components/pagePreloader'
import {
  useWebhookInstrument,
  WebhookInstrumentRecord,
} from 'hooks/webhook-instrument'
import { formatDateTimeInTimeZone, getUpdatedSearchParams, parseSearchParams } from 'services/util'

const displayValue = (value?: string | null) => value || '-'

const formatPayload = (payload: string) => {
  if (!payload) return '-'
  try {
    return JSON.stringify(JSON.parse(payload), null, 2)
  } catch {
    return payload
  }
}

const WebhookInstrument: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams: Record<string, any> = parseSearchParams(searchParams, 'filter')
  const [rowSelection, setRowSelection] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isPayloadOpen, setIsPayloadOpen] = useState(false)
  const [selectedPayload, setSelectedPayload] = useState('')

  const formHook = useForm<FieldValues>({
    defaultValues: {
      pagination: filterParams?.pagination || { pageIndex: 0, pageSize: 10 },
      sort: filterParams?.sort || null,
      fields: filterParams?.fields || [],
    },
  })

  const { data, isLoading, isFetching, error, refetch } = useWebhookInstrument(
    filterParams,
    { keepPreviousData: true }
  )
  const responseData = data?.data

  const openPayload = (payload: string) => {
    setSelectedPayload(formatPayload(payload))
    setIsPayloadOpen(true)
  }

  const columns: ColumnDef<WebhookInstrumentRecord>[] = useMemo(
    () => [
      {
        header: 'Platform',
        accessorKey: 'platform',
        enableSorting: false,
        cell: ({ row }) => displayValue(row.original.platform),
      },
      {
        header: 'Event',
        accessorKey: 'event',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="max-w-[280px] truncate" title={row.original.event}>
            {displayValue(row.original.event)}
          </div>
        ),
      },
      {
        header: 'Reference Id',
        accessorKey: 'referenceId',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="max-w-[220px] truncate font-mono text-xs" title={row.original.referenceId}>
            {displayValue(row.original.referenceId)}
          </div>
        ),
      },
      {
        header: 'Order Id',
        accessorKey: 'orderId',
        enableSorting: false,
        cell: ({ row }) => displayValue(row.original.orderId),
      },
      {
        header: 'Request Date',
        accessorKey: 'requestDatetime',
        enableSorting: false,
        cell: ({ row }) => formatDateTimeInTimeZone(row.original.requestDatetime),
      },
      {
        header: 'Response Date',
        accessorKey: 'responseDatetime',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="whitespace-nowrap">{formatDateTimeInTimeZone(row.original.responseDatetime)}</div>
        ),
      },
      {
        header: 'Action',
        id: 'action',
        enableSorting: false,
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => openPayload(row.original.payload)}
            className="rounded p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-slate-700"
            title="View payload"
            aria-label="View payload"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
        ),
      },
    ],
    []
  )

  useEffect(() => {
    const subscriber = formHook.watch((value, { name }) => {
      if (!name) return
      const updatedParams = getUpdatedSearchParams(filterParams, value, name)
      if (Object.keys(updatedParams).length) {
        setSearchParams({ filter: encodeURIComponent(JSON.stringify(updatedParams)) })
      } else {
        setSearchParams({})
      }
    })
    return () => subscriber.unsubscribe()
  }, [filterParams, formHook, setSearchParams])

  const handleRefresh = () => {
    setIsRefreshing(true)
    refetch().finally(() => setIsRefreshing(false))
  }

  if (isLoading) return <PagePreloader />

  if (error) {
    return (
      <SmoothTransition>
        <div className="flex h-64 items-center justify-center text-lg text-red-600">
          Error: {error instanceof Error ? error.message : 'An error occurred'}
        </div>
      </SmoothTransition>
    )
  }

  return (
    <SmoothTransition>
      <div className="w-full rounded-lg border border-gray-200 bg-white px-6 pt-2 shadow-md dark:border-slate-800 dark:bg-slate-800">
        <CustomTable
          columns={columns}
          data={responseData?.results || []}
          pages={responseData?.pages || 0}
          rowSelectionState={[rowSelection, setRowSelection]}
          isLoading={isFetching || isRefreshing}
          isErrored={!!error}
          formHook={formHook}
          title="Webhook Log"
          onRefresh={handleRefresh}
          totalRecords={responseData?.total}
          titleLabel={`Total ${responseData?.total || 0} records`}
          searchVisible={false}
        />
      </div>

      <DialogWrapper
        isOpen={isPayloadOpen}
        setIsOpen={setIsPayloadOpen}
        title="Webhook Payload"
        panelClass="max-w-4xl"
      >
        <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-slate-100 p-4 text-xs text-slate-900 dark:bg-slate-900 dark:text-slate-100">
          {selectedPayload}
        </pre>
      </DialogWrapper>
    </SmoothTransition>
  )
}

export default WebhookInstrument
